/**
 * MODULE: core/tenant/frontend/hooks/useTenant.ts
 * PURPOSE: useTenant hook - Provides access to tenant context (current tenant, available tenants, switch function)
 *
 * DEPENDENCIES:
 *   - react: useContext for accessing React context
 *   - ../components/TenantProvider: TenantContext for tenant state
 *   - ../../contracts/tenant.types: TenantContextValue type
 *
 * PUBLIC API:
 *   - useTenant(): Returns tenant context value (currentTenant, availableTenants, switchTenant, etc.)
 *
 * SIDE EFFECTS:
 *   - Reads tenant context from TenantProvider
 *   - Throws error if used outside TenantProvider
 *
 * AGENT SAFETY: SAFE
 * FILE SIZE: 14 / 100 lines
 *
 * CHANGE LOG:
 *   - 2026-01-20: Created - useTenant hook for accessing tenant context
 *     Provides convenient access to tenant state
 *     Validates hook is used within TenantProvider
 *     Related: TenantProvider, TenantSwitcher, TenantGuard
 *
 * See: /README.md#documentation | /ARCHITECTURE.md | /core/tenant/README.md
 */

import { useContext } from 'react';
import { TenantContext } from '../components/TenantProvider';
import type { TenantContextValue } from '../../contracts/tenant.types';

export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);

  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }

  return context;
}
